import { formatPrice } from "@/lib/price";
import L, { Icon, LatLngTuple, Map } from "leaflet";
import { JSX } from "react";
import { Marker, Popup } from "react-leaflet";
import MapAtomPopupContent from "./MapAtomPopupContent";
import { OutputNode, OutputNodeAtom } from "@/types/real-estate";
import { COUNTY_THRESHOLD, DesiredLevel, DISTRICT_THRESHOLD, FLYTO_OFFSET_CLUSTER, FLYTO_OFFSET_CLUSTER_MOBILE, FLYTO_OFFSET_SINGLE, FLYTO_OFFSET_SINGLE_FULLMAP, PARISH_THRESHOLD } from "../map.constants";
import { MapListCluster } from "./map-list-cluster/MapListCluster";
const heartSvg12 = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="rgba(255, 0, 0, 0.6)"
    class="lucide lucide-heart-icon lucide-heart"
    style="transform: translateY(0.5px);"
  >
    <path d="M10.5 2c-1-1-2.6-1-3.6 0L6 2.9 5.1 2C4.1 1 2.5 1 1.5 2s-1 2.6 0 3.6L6 10l4.5-4.4c1-1 1-2.6 0-3.6z"/>
  </svg>
  `;
const iconSvg = `
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    width="16" 
    height="16" 
    fill="none" 
    stroke="white" 
    stroke-width="2" 
    stroke-linecap="round" 
    stroke-linejoin="round"
  >
    <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
  </svg>
`;
const seekerSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="15" height="15"
    fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/>
  </svg>
`;
export const createMarkerSVGIcon = (value?: string | number, type?: "price" | "house" | "cluster" | "demand", isFavorite?: boolean) => {
  if (type === "demand") {
    const diameter = 28;
    const extraZones = Number(value) || 0;
    const htmlContent = `
      <div style="
        position: relative;
        background: linear-gradient(45deg, #6d28d9 0%, #8b5cf6 100%);
        border: 2px solid hsl(var(--card));
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${diameter}px;
        height: ${diameter}px;
      ">
        <div style="position: relative; z-index: 10;">${seekerSvg}</div>
        ${extraZones > 0 ? `<div style="
                position: absolute;
                bottom: -5px;
                right: -5px;
                min-width: 16px;
                height: 16px;
                padding: 0 3px;
                background: #4c1d95;
                color: white;
                border: 1.5px solid white;
                border-radius: 9px;
                font-size: 10px;
                font-weight: 700;
                line-height: 13px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 1px 3px rgba(0,0,0,0.4);
              ">+${extraZones}</div>` : ""}
      </div>
    `;
    return new L.DivIcon({
      html: htmlContent,
      className: "",
      iconSize: [diameter, diameter],
      iconAnchor: [diameter / 2, diameter / 2],
      popupAnchor: [0, -diameter / 2]
    });
  }
  if (type === "house") {
    const diameter = 28;
    const iconSize = 16;
    const htmlContent = `
      <div style="
        position: relative;
        background: linear-gradient(45deg, #FFA500 0%, #FFA500 30%, #fac25c 70%, #fac25c 100%);
        border: 2px solid hsl(var(--card));
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${diameter}px;
        height: ${diameter}px;
      ">
        <!-- Place icon SVG with a lower z-index -->
        <div style="position: relative; z-index: 10;">
          ${iconSvg}
        </div>
        
        <!-- Place heart icon with a higher z-index -->
        ${isFavorite ? `<div style="
                display: flex;
                align-items: center;
                justify-content: center;    
                width: 14px;
                height: 14px;
                background-color: white;
                position: absolute;
                z-index: 30;
                bottom: -4px;
                right: -4px;
                border-radius: 50%;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                overflow: hidden;
              "
              >${heartSvg12}</div>` : ""}
      </div>
    `;
    return new L.DivIcon({
      html: htmlContent,
      className: "",
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconSize / 2, iconSize],
      popupAnchor: [0, -iconSize / 2]
    });
  }
  if (type === "price") {
    const text = value === 0 ? "Consultar €" : formatPrice(value?.toString());
    const textLength = text.length;
    const widthDefault = 50;
    const widthText = textLength * 8;
    const computedWidth = text ? widthText : widthDefault;
    const heightDefault = 40;
    const heightText = 20;
    const computedHeight = text ? heightText : heightDefault;
    const tailHeight = 5;
    const totalHeight = computedHeight + tailHeight;
    const padding = 0;
    const iconWidth = computedWidth + padding * 2;
    const iconHeight = totalHeight + padding * 2;
    const htmlContent = `
      <div style="
      position: relative;
        padding: ${padding}px;
        background: hsl(var(--card));

        border-radius: 18px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${computedWidth}px;
        height: ${totalHeight}px;
      ">
        <span style="
          font-size: 12px;
          font-weight: bold;
          color: hsl(var(--foreground));
        ">${text}</span>
         ${isFavorite ? `<div style="
                display: flex;
                padding: 2px 3px;
                align-items: center;
                justify-content: center;    
               
                background-color: white;
                position: absolute;
                z-index: 30;
                bottom: -8px;
                right: -4px;
                border-radius: 40%;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                overflow: hidden;
              "
              >${heartSvg12}</div>` : ""}
      </div>
    `;
    return new L.DivIcon({
      html: htmlContent,
      className: "",
      // or a custom class if you prefer
      iconSize: [iconWidth, iconHeight],
      iconAnchor: [iconWidth / 2, iconHeight],
      popupAnchor: [0, -iconHeight / 2]
    });
  } else if (type === "cluster") {
    const text = value?.toString();
    const diameter = 28;
    const htmlContent = `
      <div style="
        background: linear-gradient(45deg, #1E3A8A 0%, #3B82F6 100%);
        color: white;
        border: 2px solid hsl(var(--card));
        border-radius: 50%;
        box-shadow: 0 2px 5px rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${diameter}px;
        height: ${diameter}px;
        font-weight: bold;
        font-size: 12px;
      ">${text}</div>
    `;
    return new L.DivIcon({
      html: htmlContent,
      className: "",
      iconSize: [diameter, diameter],
      iconAnchor: [diameter / 2, diameter / 2],
      popupAnchor: [0, -diameter / 2]
    });
  } else {
    const text = value?.toString();
    const diameter = 28;
    const color = "hsl(var(--foreground))";
    const htmlContent = `
      <div style="
        background: hsl(var(--card));
border: 2px solid hsl(var(--card));
        border-radius: 18px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${diameter}px;
        height: ${diameter}px;
      ">
        <span style="
          font-size: 12px;
          font-weight: bold;
          color: ${color};
        ">${text}</span>
      </div>
    `;
    return new L.DivIcon({
      html: htmlContent,
      className: ""
    });
  }
};
export function getDesiredLevel(zoom: number): DesiredLevel {
  if (zoom < DISTRICT_THRESHOLD) {
    return DesiredLevel.District;
  } else if (zoom < COUNTY_THRESHOLD) {
    return DesiredLevel.County;
  } else if (zoom < PARISH_THRESHOLD) {
    return DesiredLevel.Parish;
  } else {
    return DesiredLevel.RealEstate;
  }
}
interface ProcessResult {
  markers: JSX.Element[];
  coords: LatLngTuple[];
}

/**
 * Processes a list of nodes to generate map markers and coordinates based on the desired level and map bounds.
 *
 * @param nodes - An array of nodes, which can be either `OutputNode` or `OutputNodeAtom` types.
 * @param desiredLevel - The desired administrative level or type of data to process (e.g., "realEstate").
 * @param map - The Leaflet map instance used to determine bounds and perform map operations.
 * @param fullmap - A boolean indicating whether the map is in full-screen mode.
 * @param isMobile - A boolean indicating whether the application is being viewed on a mobile device.
 * @param callbackTrigger - A callback function triggered when a marker is clicked, passing the `realEstateId`.
 * @param parentKey - A string used to generate unique keys for markers, typically passed recursively (default is an empty string).
 * @returns An object containing:
 *   - `markers`: An array of JSX elements representing the generated map markers.
 *   - `coords`: An array of latitude and longitude tuples representing the coordinates of the markers.
 */
export function processNodes(nodes: OutputNode[] | OutputNodeAtom[], desiredLevel: DesiredLevel, map: L.Map, fullmap: boolean, isMobile: boolean, callbackTrigger: (realEstateId: string) => void, parentKey = "", onDemandSelect?: (realEstateId: string) => void): ProcessResult {
  const markers: JSX.Element[] = [];
  const coords: LatLngTuple[] = [];
  const bounds = map.getBounds();
  // Track properties at the same coordinates for clustering
  const coordClusters: Record<string, OutputNodeAtom[]> = {};

  // A demand seeker pin: distinct glyph + "+N" for extra zones. Clicking it
  // selects the seeker (forcedMapTarget) so the map reveals their zone polygons.
  const demandMarker = (node: OutputNodeAtom, markerKey: string) => <Marker key={markerKey} position={[node.lat, node.lng]} icon={createMarkerSVGIcon((node.zones?.length ?? 1) - 1, "demand")} eventHandlers={{
    click: e => {
      e.target.openPopup();
      onDemandSelect?.(node.realEstateId);
    }
  }} data-sentry-element="Marker" data-sentry-component="demandMarker" data-sentry-source-file="mapUtils.tsx">
      {(!fullmap || isMobile) && <Popup closeButton={false} className="single-property-popup">
          <div style={{
        width: "350px"
      }}>
            <MapAtomPopupContent node={node} />
          </div>
        </Popup>}
    </Marker>;
  nodes.forEach((node, index) => {
    const key = `${parentKey}-${index}`;
    if ("realEstateId" in node) {
      if (node.lat && node.lng) {
        const nodeLatLng = L.latLng(node.lat, node.lng);
        if (!bounds.contains(nodeLatLng)) {
          return;
        }

        // Only render individual markers if not at RealEstate level
        if (desiredLevel !== DesiredLevel.RealEstate) {
          const value = undefined;
          const marker = "house";
          // Use a stable key based on realEstateId and coordinates
          const markerKey = `${node.realEstateId}-${node.lat}-${node.lng}`;
          if (node.isDemand) {
            markers.push(demandMarker(node, markerKey));
            coords.push([node.lat, node.lng]);
            // Demand seekers never coord-cluster with supply price markers.
            return;
          }
          markers.push(<Marker key={markerKey} position={[node.lat, node.lng]} icon={createMarkerSVGIcon(value, marker, node.isFavorite)} eventHandlers={{
            click: e => {
              // Open popup first
              e.target.openPopup();
              // Then trigger callback and flyTo
              callbackTrigger(node.realEstateId);
              const offsetY = fullmap && !isMobile ? FLYTO_OFFSET_SINGLE_FULLMAP : FLYTO_OFFSET_SINGLE;
              // Small delay to ensure popup is open
              setTimeout(() => {
                mapFlyTo({
                  map,
                  lat: node.lat,
                  lng: node.lng,
                  offsetY
                });
              }, 50);
            }
          }}>
              {(!fullmap || isMobile) && <Popup closeButton={false} className="single-property-popup">
                  <div style={{
                width: "350px"
              }}>
                    <MapAtomPopupContent node={node} />
                  </div>
                </Popup>}
            </Marker>);
          coords.push([node.lat, node.lng]);
        }

        // Group properties by coordinates for clustering at realEstate level
        const coordKey = `${node.lat.toFixed(6)},${node.lng.toFixed(6)}`;
        if (!coordClusters[coordKey]) {
          coordClusters[coordKey] = [];
        }
        coordClusters[coordKey].push(node);
      }
    } else {
      if ((node.administrative_level === desiredLevel || desiredLevel === DesiredLevel.District || desiredLevel === DesiredLevel.County) && node.count > 1) {
        if (node.lat && node.lng) {
          markers.push(<Marker key={key} position={[node.lat, node.lng]} icon={createMarkerSVGIcon(node.count)} eventHandlers={{
            click: () => {
              map.setView([node.lat, node.lng], map.getZoom() + 1);
            }
          }} />);
          coords.push([node.lat, node.lng]);
        }
      } else if (node.children && node.children.length > 0) {
        const result = processNodes(node.children as OutputNode[], desiredLevel, map, fullmap, isMobile, callbackTrigger, key, onDemandSelect);
        markers.push(...result.markers);
        coords.push(...result.coords);
      }
    }
  });
  if (desiredLevel === DesiredLevel.RealEstate) {
    Object.entries(coordClusters).forEach(([coordKey, clusterNodes], index) => {
      const [latStr, lngStr] = coordKey.split(",");
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);

      // Skip points outside the visible bounds
      const nodeLatLng = L.latLng(lat, lng);
      if (!bounds.contains(nodeLatLng)) return;
      if (clusterNodes.length === 1) {
        // Single property: show price marker
        const node = clusterNodes[0];
        // Use a stable key based on realEstateId and coordinates
        const markerKey = `${node.realEstateId}-${lat}-${lng}`;
        if (node.isDemand) {
          markers.push(demandMarker(node, markerKey));
          coords.push([lat, lng]);
          return;
        }
        markers.push(<Marker key={markerKey} position={[lat, lng]} icon={createMarkerSVGIcon(node.price, "price", node.isFavorite)} eventHandlers={{
          click: e => {
            e.target.openPopup();
            callbackTrigger(node.realEstateId);
            const offsetY = fullmap && !isMobile ? FLYTO_OFFSET_SINGLE_FULLMAP : FLYTO_OFFSET_SINGLE;
            setTimeout(() => {
              mapFlyTo({
                map,
                lat,
                lng,
                offsetY
              });
            }, 50);
          }
        }}>
            {(isMobile || !fullmap) && <Popup closeButton={false} autoPan={false} className="single-property-popup">
                <div style={{
              width: "350px"
            }}>
                  <MapAtomPopupContent node={node} />
                </div>
              </Popup>}
          </Marker>);
      } else if (clusterNodes.length > 1) {
        // Multiple properties: show cluster marker
        const markerKey = `${parentKey}-cluster-${index}-multi`;
        markers.push(<Marker key={markerKey} position={[lat, lng]} icon={createMarkerSVGIcon(clusterNodes.length, "cluster", false)} eventHandlers={{
          click: e => {
            e.target.openPopup();
            const clusterOffsetY = isMobile ? FLYTO_OFFSET_CLUSTER_MOBILE : FLYTO_OFFSET_CLUSTER;
            setTimeout(() => {
              const popup = e.target.getPopup();
              mapFlyTo({
                map,
                lat,
                lng,
                offsetY: clusterOffsetY,
                onAnimationComplete: () => {
                  if (popup && !popup.isOpen()) {
                    e.target.openPopup();
                  }
                }
              });
            }, 50);
          }
        }}>
            <Popup closeButton={false} autoPan={false}>
              <div className="p-2 overflow-hidden" style={{
              width: isMobile ? "90vw" : "500px"
            }}>
                <MapListCluster lat={lat} lng={lng} realEstateIds={clusterNodes.map(n => n.realEstateId)} />
              </div>
            </Popup>
          </Marker>);
      }
      coords.push([lat, lng]);
    });
  }
  return {
    markers,
    coords
  };
}
export function mapFlyTo({
  map,
  lat,
  lng,
  offsetY,
  onAnimationComplete
}: {
  map: Map;
  lat: number;
  lng: number;
  offsetY: number;
  onAnimationComplete?: () => void;
}) {
  const target = L.latLng(lat, lng);
  const zoomLevel = PARISH_THRESHOLD + 2;
  const targetPoint = map.project(target, zoomLevel);
  const adjustedPoint = targetPoint.subtract([0, offsetY]);
  const newCenter = map.unproject(adjustedPoint, zoomLevel);
  map.flyTo(newCenter, zoomLevel, {
    animate: true,
    duration: 0.4
  });

  // Execute callback after animation completes
  if (onAnimationComplete) {
    const onMoveEnd = () => {
      onAnimationComplete();
      map.off("moveend", onMoveEnd);
    };
    map.on("moveend", onMoveEnd);
  }
}